import { TrendingUp, RotateCcw } from 'lucide-react'
import type { GrowthData } from '@/api/dashboard'
import { LatestAchievementBadge } from '@/components/achievements/LatestAchievementBadge'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'

interface GrowthHighlightProps {
  growth: GrowthData
}

export function GrowthHighlight({ growth }: GrowthHighlightProps) {
  const { dashboard } = useMinCopy()

  if (growth.vocabularyCount === 0) return null

  const hasRecovered = growth.recoveredWords.length > 0
  const hasDelta = growth.vocabularyDelta30d > 0
  const hasLatestAchievement = growth.latestAchievement != null

  if (!hasRecovered && !hasDelta && growth.wordsRefreshedToday === 0 && !hasLatestAchievement) {
    return null
  }

  return (
    <div className="flex flex-wrap gap-3" data-testid="growth-highlight">
      {hasDelta ? (
        <div className="flex min-w-[180px] flex-1 items-center gap-3 rounded-lg border bg-muted/20 px-4 py-3">
          <TrendingUp className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              {formatMessage(dashboard.wordsThisMonth, { count: growth.vocabularyDelta30d })}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatMessage(dashboard.totalInVocabulary, { count: growth.vocabularyCount })}
            </p>
          </div>
        </div>
      ) : null}

      {growth.wordsRefreshedToday > 0 ? (
        <div className="flex min-w-[180px] flex-1 items-center gap-3 rounded-lg border bg-muted/20 px-4 py-3">
          <RotateCcw className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0">
            <p className="text-sm font-semibold">
              {growth.wordsRefreshedToday === 1
                ? dashboard.wordsRefreshedTodayOne
                : formatMessage(dashboard.wordsRefreshedToday, {
                    count: growth.wordsRefreshedToday,
                  })}
            </p>
            <p className="text-xs text-muted-foreground">{dashboard.keepMomentum}</p>
          </div>
        </div>
      ) : null}

      {hasLatestAchievement && growth.latestAchievement ? (
        <LatestAchievementBadge achievement={growth.latestAchievement} compact />
      ) : null}

      {hasRecovered ? (
        <div className="flex min-w-[220px] flex-[2] items-start gap-3 rounded-lg border bg-muted/20 px-4 py-3">
          <TrendingUp className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
          <div className="min-w-0">
            {growth.recoveredWords.slice(0, 1).map((rw) => (
              <p key={rw.word} className="text-sm font-medium leading-snug">
                {formatMessage(dashboard.yesterdayMissed, {
                  word: rw.word,
                  correct: rw.correctToday,
                })}
              </p>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
